import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Play, Pause, Volume2, VolumeX, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const VideoCard = ({ video }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      
      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, []);

  return (
    <Card className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300 group">
      <div 
        className="relative aspect-[9/16] bg-black overflow-hidden cursor-pointer"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(isPlaying ? false : true)}
      >
        <video
          ref={videoRef}
          className="w-full h-full object-contain"
          muted={isMuted}
          playsInline
          preload="metadata"
          onClick={togglePlay}
        >
          <source src={video.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Video Controls Overlay */}
        <div 
          className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Play/Pause Button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={togglePlay}
              className="w-16 h-16 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all hover:scale-110"
              aria-label={isPlaying ? 'Pause video' : 'Play video'}
            >
              {isPlaying ? (
                <Pause className="w-8 h-8 text-white" />
              ) : (
                <Play className="w-8 h-8 text-white ml-1" />
              )}
            </button>
          </div>

          {/* Mute/Unmute Button */}
          <div className="absolute top-4 right-4">
            <button
              onClick={toggleMute}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all"
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5 text-white" />
              ) : (
                <Volume2 className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
      <CardContent className="p-4 bg-white">
        <p className="text-sm text-center text-gray-600">{video.caption}</p>
      </CardContent>
    </Card>
  );
};

const InfluencerSection = () => {
  const [currentMobileSlide, setCurrentMobileSlide] = useState(0);
  
  const influencerVideos = useMemo(() => [
    {
      id: 1,
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/77k17lnh_WhatsApp%20Video%202025-02-25%20at%203.45.21%20PM.mp4',
      caption: 'Creator visit at our Delhi showroom',
      alt: 'Influencer showcasing Vista Fashion blinds at All Heaven Interior showroom'
    },
    {
      id: 2,
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/o6bp05wn_WhatsApp%20Video%202025-02-25%20at%204.04.37%20PM.mp4',
      caption: 'Vista Fashion blinds installation experience',
      alt: 'Real customer experience with Vista Fashion window blinds installation'
    },
    {
      id: 3,
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/ydb33lki_WhatsApp%20Video%202025-02-25%20at%2012.37.05%20PM.mp4',
      caption: 'Real interiors, real installations',
      alt: 'Authentic interior design project showcase in East Delhi'
    },
    {
      id: 4,
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/kefucbrc_WhatsApp%20Video%202025-02-25%20at%2012.39.10%20PM.mp4',
      caption: 'Premium quality products and service',
      alt: 'Customer testimonial for All Heaven Interior services'
    }
  ], []);

  const goToNextSlide = useCallback(() => {
    setCurrentMobileSlide((prev) => (prev + 1) % influencerVideos.length);
  }, [influencerVideos.length]);

  const goToPreviousSlide = useCallback(() => {
    setCurrentMobileSlide((prev) => (prev - 1 + influencerVideos.length) % influencerVideos.length);
  }, [influencerVideos.length]);

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Social Proof
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Trusted by Creators & Influencers
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Real experiences from creators who visited our showroom and trusted our services
          </p>
        </div>

        {/* Desktop Grid (2x2) */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {influencerVideos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentMobileSlide * 100}%)` }}
            >
              {influencerVideos.map((video) => (
                <div key={video.id} className="w-full flex-shrink-0 px-4">
                  <VideoCard video={video} />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation */}
          <button
            onClick={goToPreviousSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-all"
            aria-label="Previous video"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <button
            onClick={goToNextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-all"
            aria-label="Next video"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>

          {/* Mobile Indicators */}
          <div className="flex justify-center space-x-2 mt-6">
            {influencerVideos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentMobileSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentMobileSlide
                    ? 'w-8 bg-amber-600'
                    : 'w-2 bg-gray-300'
                }`}
                aria-label={`Go to video ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Trust Badge */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500 italic">
            Authentic experiences • Real installations • Trusted quality
          </p>
        </div>
      </div>
    </section>
  );
};

export default InfluencerSection;
